/**
 * PURE core of the taxa stacked-bar series shaping.
 *
 * React/Redux/DOM-free so the live UI and the server-side render sidecar can share one source of truth
 * for this shaping logic: given the already-filtered buckets (the `selectedPath` prefix + grade filter
 * ran upstream in `filterCubeByPathAndGrade`), it aggregates per-bucket taxa at the zoom level,
 * derives a stable stack order, applies optional percentage scaling, and collapses the unreadable
 * long tail into "Other" — byte-identical to what the selector produced inline before.
 */
import type { FilteredBucketValues } from './CubeFiltering.ts'
import { aggregateTaxaAllBuckets } from './TaxaAggregationCore.ts'
import { collapseStackedSeries } from './CollapseSeries.ts'
import { axisLabelOf } from './axisLabel.js'

export type TaxaStackedBarSeries = {
  data: Array<Record<string, number | string>>
  stackKeys: string[]
  /**
   * Samples per bucket, aligned to `data` by index.
   *
   * Beside the rows rather than on them: a row is a flat `{ xAxisvalue, [taxon]: number }` map, and
   * the area/line sinks build their trait rows by spreading everything that is not `xAxisvalue` into
   * the series data — a count carried on the row would come back as a phantom taxon.
   */
  sampleCounts: (number | undefined)[]
}

/**
 * Pre-collapse rows: one row per bucket `{ xAxisvalue: label, [taxon]: number, … }` plus the sorted
 * `stackKeys`. Kept separate so the (dev-only) sub-pixel-series diagnostic in the selector can still
 * measure the FULL set before {@link collapseStackedSeries} trims it.
 */
export function computeStackedBarRows(opts: {
  filteredBuckets: FilteredBucketValues[]
  level: number
  isPercent: boolean
}): TaxaStackedBarSeries {
  const { filteredBuckets, level, isPercent } = opts

  const agg = aggregateTaxaAllBuckets({ filteredBuckets, level })

  // Stable stack order avoids chart churn between renders
  const stackKeys = [...agg.taxa].sort((a, b) => a.localeCompare(b))

  const data = agg.rows.map((r) => {
    const out: Record<string, number | string> = { xAxisvalue: axisLabelOf(r.bucketLabel) }

    let sum = 0
    if (isPercent) {
      for (let i = 0; i < stackKeys.length; i++) {
        sum += r.data[stackKeys[i]!] ?? 0
      }
    }
    const factor = isPercent && sum > 0 ? 100 / sum : 1

    for (let i = 0; i < stackKeys.length; i++) {
      const k = stackKeys[i]!
      const v = r.data[k] ?? 0
      out[k] = factor === 1 ? v : v * factor
    }
    return out
  })

  return { data, stackKeys, sampleCounts: agg.rows.map((r) => r.sampleCount) }
}

/**
 * Full stacked-bar series for a chart: {@link computeStackedBarRows} followed by the long-tail
 * "Other" collapse. This is the single entry point the SSR sidecar calls; the selector calls
 * {@link computeStackedBarRows} directly so it can run its diagnostic on the pre-collapse set.
 */
export function buildTaxaStackedBarSeries(opts: {
  filteredBuckets: FilteredBucketValues[]
  level: number
  isPercent: boolean
}): TaxaStackedBarSeries {
  const { data, stackKeys, sampleCounts } = computeStackedBarRows(opts)
  // collapseStackedSeries rewrites series within each row; it never adds, drops or reorders rows, so
  // the counts stay aligned by index.
  const collapsed = collapseStackedSeries(data, stackKeys)
  return { data: collapsed.data, stackKeys: collapsed.stackKeys, sampleCounts }
}
